import React, { useEffect, useState } from "react";
import { Footprints, MapPin, ExternalLink, Loader2, AlertCircle, ChevronDown, Plus, UserCheck, Sparkles, Users, Baby, Dog, TrendingUp } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import SubmitWalkModal from "./SubmitWalkModal";
import { matchOperatorBadge } from "../../lib/operatorBadges";

/**
 * OperatorBadge — small chip showing the landowner / trail authority's own
 * mark (National Trust, Forestry England, RSPB…) when OSM's `operator` tag
 * matches a known UK body. Silently returns null on 404 / unmatched.
 */
function OperatorBadge({ operator, testid }) {
  const [failed, setFailed] = React.useState(false);
  const match = matchOperatorBadge(operator);
  if (!match || failed) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 bg-white border hairline px-2 py-1 text-[10px] mono uppercase tracking-wider text-[#333]"
      data-testid={testid || "operator-badge"}
      title={`Operated by ${match.name}`}
    >
      <img
        src={match.logoUrl}
        alt={`${match.name} logo`}
        className="w-4 h-4 object-contain shrink-0"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
      {match.name}
    </span>
  );
}

/**
 * iter108 — Local Walks Nearby.
 *
 * Discovers named hiking / foot routes within 10 km of a campsite using
 * the backend Overpass-powered endpoint. Renders a prominent card with
 * the top 5 walks (with "Show all within 10 km" expand) — each walk has:
 *   • Name + descriptors (Hiking, National trail, etc.)
 *   • Distance from campsite + total length (when OSM has it)
 *   • Embedded OSM mini-map with the start pin
 *   • "Open in Maps" CTA that fires the platform default maps app
 *
 * Placed high on the campsite detail page (right after weather) so it's
 * never scrolled-to.
 */

function openInMaps(lat, lng, name) {
  // Universal Google Maps URL works on iOS, Android, desktop — opens
  // Apple Maps via system handler on iOS Safari for many users.
  const q = encodeURIComponent(name || `${lat},${lng}`);
  const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${q}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function MiniMap({ lat, lng, name }) {
  // Free OSM embed via openstreetmap.org's export iframe. No JS, no API
  // key, no rate limit — just an HTML iframe.
  const span = 0.012; // ≈ 1 km box
  const bbox = `${lng - span},${lat - span},${lng + span},${lat + span}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  return (
    <iframe
      title={`Map of ${name}`}
      src={src}
      className="w-full h-32 border hairline bg-[#f5f5f5]"
      loading="lazy"
      data-testid="walk-minimap"
    />
  );
}

/**
 * iter110 — Render structured suitability + difficulty badges with icons.
 * Falls back to the raw `descriptors[]` for anything else (route type,
 * national-trail status, etc.).
 */
function WalkBadges({ walk }) {
  const badges = [];
  // Difficulty colour-codes
  const diff = walk.difficulty;
  if (diff === "Easy") badges.push({ key: "diff", label: "Easy", cls: "bg-[#e8f7ed] text-[#0a7f3f]" });
  else if (diff === "Moderate") badges.push({ key: "diff", label: "Moderate", cls: "bg-[#fff4d8] text-[#a86d00]" });
  else if (diff === "Hard") badges.push({ key: "diff", label: "Hard", cls: "bg-[#ffe4e0] text-[#b00020]" });

  if (walk.family_friendly) {
    badges.push({ key: "family", icon: Users, label: "Family-friendly", cls: "bg-[#e8f7ed] text-[#0a7f3f]" });
  }
  if (walk.child_friendly) {
    badges.push({ key: "child", icon: Baby, label: "Child-friendly", cls: "bg-[#e8f7ed] text-[#0a7f3f]" });
  }
  if (walk.dog_friendly) {
    badges.push({ key: "dog", icon: Dog, label: "Dogs welcome", cls: "bg-[#e8f7ed] text-[#0a7f3f]" });
  }
  if (walk.incline) {
    const cls =
      walk.incline === "Flat" ? "bg-[#e8f7ed] text-[#0a7f3f]" :
      walk.incline === "Gentle" ? "bg-[#eef5ff] text-[#1a4d8a]" :
      walk.incline === "Moderate" ? "bg-[#fff4d8] text-[#a86d00]" :
      "bg-[#ffe4e0] text-[#b00020]";
    badges.push({ key: "incline", icon: TrendingUp, label: walk.incline, cls });
  }

  // Append OSM-only descriptors that aren't already covered by structured badges
  const covered = new Set(["Easy", "Moderate", "Hard", "Family-friendly", "Child-friendly", "Dogs welcome"]);
  for (const d of (walk.descriptors || [])) {
    // Skip incline descriptors (already shown) and ones covered above
    if (d.startsWith("Incline:") || covered.has(d)) continue;
    badges.push({ key: d, label: d, cls: "bg-[#f5f5f5] text-[#444]" });
  }

  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="walk-badges">
      {badges.map((b) => {
        const I = b.icon;
        return (
          <span
            key={b.key + b.label}
            className={`inline-flex items-center gap-1 ${b.cls} text-[10px] mono uppercase tracking-wider px-1.5 py-0.5`}
          >
            {I ? <I className="w-3 h-3" strokeWidth={2} /> : null}
            {b.label}
          </span>
        );
      })}
    </div>
  );
}

export default function WalksNearbySection({ campsiteId, campsiteName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!campsiteId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    const url = showAll
      ? `/campsites/${campsiteId}/walks?all=true`
      : `/campsites/${campsiteId}/walks`;
    api.get(url, { timeout: 40_000 })
      .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
      .catch((e) => {
        if (alive) { setError(formatErr(e)); setLoading(false); }
      });
    return () => { alive = false; };
  }, [campsiteId, showAll, reloadKey]);

  if (!campsiteId) return null;

  const openSubmit = () => {
    if (!user) {
      // Send them to login, then back here
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setSubmitOpen(true);
  };

  return (
    <div className="mb-10" data-testid="walks-nearby-section">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2 !text-[#0a7f3f]">
            <Footprints className="w-3 h-3" />
            Walks Nearby
          </div>
          <h3 className="font-display text-xl sm:text-2xl tracking-tight mt-1">
            Local walks within 10 km
          </h3>
          {data?.total > 0 && (
            <p className="text-xs text-[#666] mt-1">
              {data.total} route{data.total === 1 ? "" : "s"} found via OpenStreetMap
            </p>
          )}
        </div>
      </div>

      {loading && (
        <div className="border hairline bg-white p-6 flex items-center gap-3 text-sm text-[#666]" data-testid="walks-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Finding walks near {campsiteName || "this campsite"}…
        </div>
      )}

      {error && (
        <div className="border hairline bg-[#fff8f5] p-4 flex items-start gap-3 text-sm" data-testid="walks-error">
          <AlertCircle className="w-4 h-4 text-[#b00020] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display">Couldn't load walks right now</div>
            <div className="text-[#666] text-xs mt-1">{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && data && data.walks?.length === 0 && (
        <div className="border hairline bg-white p-6 text-center" data-testid="walks-empty">
          <Footprints className="w-8 h-8 text-[#888] mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-[#666] mb-3">
            No named walking routes found within 10 km on OpenStreetMap yet.
            Know a great local walk? Be the first to add it.
          </p>
          <button
            type="button"
            onClick={openSubmit}
            className="inline-flex items-center gap-1.5 bg-[#0a7f3f] text-white py-2 px-4 text-[11px] uppercase tracking-wider font-bold shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform"
            data-testid="walks-submit-cta-empty"
          >
            <Plus className="w-3 h-3" strokeWidth={2.5} /> Submit the first walk
          </button>
        </div>
      )}

      {!loading && !error && data && data.walks?.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="walks-list">
            {data.walks.map((w, i) => {
              const isUser = w.source === "user";
              const heroPhoto = isUser && (w.photos || [])[0]?.image_data_url;
              return (
              <article
                key={w.walk_id || w.osm_id}
                className={`border hairline bg-white flex flex-col ${isUser ? "border-l-4 border-l-[#0a7f3f]" : ""}`}
                data-testid={`walk-card-${i}`}
              >
                {/* iter109 — user-submitted walks show their first photo as a hero;
                    OSM walks keep the mini-map. */}
                {heroPhoto ? (
                  <img src={heroPhoto} alt={w.name} className="w-full h-40 object-cover" />
                ) : (
                  <MiniMap lat={w.lat} lng={w.lng} name={w.name} />
                )}
                <div className="p-4 flex flex-col flex-1">
                  {isUser && (
                    <div className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-[#0a7f3f] mb-1.5" data-testid={`walk-user-badge-${i}`}>
                      <UserCheck className="w-3 h-3" /> Submitted by {w.submitted_by || "a camper"}
                    </div>
                  )}
                  <h4 className="font-display text-base leading-tight mb-2" data-testid={`walk-name-${i}`}>
                    {w.name}
                  </h4>

                  {w.operator && (
                    <div className="mb-2">
                      <OperatorBadge operator={w.operator} testid={`walk-operator-badge-${i}`} />
                    </div>
                  )}

                  {/* iter109 — AI 'what to expect' OR user description */}
                  {w.description && isUser && (
                    <p className="text-[12px] text-[#333] leading-snug mb-2" data-testid={`walk-desc-${i}`}>
                      {w.description}
                    </p>
                  )}
                  {w.ai_description && !isUser && (
                    <p className="text-[12px] text-[#333] leading-snug mb-2 italic flex gap-1.5" data-testid={`walk-ai-desc-${i}`}>
                      <Sparkles className="w-3 h-3 text-[#ff4500] flex-shrink-0 mt-0.5" />
                      <span>{w.ai_description}</span>
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <WalkBadges walk={w} />
                  </div>
                  <div className="text-xs text-[#555] space-y-0.5 mb-3">
                    {w.distance_from_campsite_km != null && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-[#0a7f3f]" strokeWidth={2} />
                        <span data-testid={`walk-distance-${i}`}>
                          <strong>{w.distance_from_campsite_km} km</strong> from campsite
                        </span>
                      </div>
                    )}
                    {w.length_km != null && (
                      <div className="flex items-center gap-1.5 pl-[18px] text-[11px] text-[#666]">
                        Route length: <strong>{w.length_km} km</strong>
                      </div>
                    )}
                    {w.operator && !matchOperatorBadge(w.operator) && (
                      <div className="flex items-center gap-1.5 pl-[18px] text-[11px] text-[#666]">
                        Operator: {w.operator}
                      </div>
                    )}
                  </div>

                  <div className="mt-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openInMaps(w.lat, w.lng, w.name)}
                      className="flex-1 bg-[#0a7f3f] text-white text-[11px] uppercase tracking-wider font-bold py-2 px-3 inline-flex items-center justify-center gap-1.5 shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform"
                      data-testid={`walk-open-maps-${i}`}
                    >
                      <MapPin className="w-3 h-3" strokeWidth={2.5} /> Open in Maps
                    </button>
                    {w.website && (
                      <a
                        href={w.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] mono text-[#555] hover:text-[#0a7f3f] inline-flex items-center gap-1 px-2 py-2 border hairline"
                        data-testid={`walk-website-${i}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Info
                      </a>
                    )}
                  </div>
                </div>
              </article>
              );
            })}
          </div>

          {!showAll && data.total > data.shown && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="btn-secondary !py-2.5 !px-4 text-xs inline-flex items-center gap-1.5"
                data-testid="walks-show-all"
              >
                Show all {data.total} walks within 10 km
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="mt-4 flex items-center justify-center">
            <button
              type="button"
              onClick={openSubmit}
              className="inline-flex items-center gap-1.5 border-2 border-[#0a7f3f] text-[#0a7f3f] hover:bg-[#0a7f3f] hover:text-white transition-colors py-2 px-4 text-[12px] uppercase tracking-wider font-bold"
              data-testid="walks-submit-cta"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
              Know a walk near here? Add it
            </button>
          </div>

          <p className="text-[10px] mono text-[#888] mt-3 text-center">
            Walk data © OpenStreetMap contributors · Open in Maps uses Google / Apple Maps
          </p>
        </>
      )}

      {submitOpen && (
        <SubmitWalkModal
          campsiteId={campsiteId}
          campsiteName={campsiteName}
          campsiteLat={data?.campsite_lat}
          campsiteLng={data?.campsite_lng}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
